import { api } from '../api/client';
import type { EscalationRow } from '../api/types';
import { article } from '../vocabulary';
import { Badge, Card, Loading, Notice, Table } from '../components';
import { useResource } from '../hooks';
import { useSession } from '../auth/session';

/**
 * Answers somebody needs to look at.
 *
 * This replaced the exception queue, and the difference is worth stating because the two look
 * similar and are not. The queue listed visits the *validation gate* had doubts about — wrong
 * place, recycled photograph, gallery upload — which are questions about whether a visit can
 * be trusted at all, and which block. This lists visits where the field user chose an answer
 * an author marked as worth escalating. The visit is sound; the answer is bad news.
 *
 * So there is no decision to make here and no queue to clear. Nothing is approved or
 * rejected. It is a report, and treating it as a workflow would invent an approval step
 * nobody asked for.
 */

export function Escalations(): JSX.Element {
  const { config } = useSession();
  const rows = useResource(() => api.get<EscalationRow[]>('/escalations'), []);

  return (
    <Card title="Escalations">
      <p className="muted">
        {/* The article is chosen from the label rather than written, because the label is a
            deployment's own word: "a store manager", but "an inspector". */}
        Visits where {article(config.fieldUserLabel)} {config.fieldUserLabel.toLowerCase()} chose
        an answer marked as worth escalating. Nothing here was blocked —{' '}
        {article(config.siteLabel)} {config.siteLabel.toLowerCase()} visit that reports a problem
        is doing its job. Mark which answers escalate on the question itself, under{' '}
        <strong>Forms &amp; questions</strong>.
      </p>

      {rows.loading ? (
        <Loading />
      ) : rows.error !== null ? (
        <Notice kind="error">{rows.error}</Notice>
      ) : (
        <Table
          rows={rows.data ?? []}
          keyOf={(row) => row.answerId}
          empty="Nothing has been escalated."
          columns={[
            { header: 'Date', render: (row) => row.scheduledDate },
            {
              header: config.siteLabel,
              // The zone rides with the place rather than taking a column of its own: most
              // forms are asked of the whole place, so a mostly-empty column would cost more
              // width than it earns — but *which* zone was rated Poor is the finding.
              render: (row) =>
                row.zoneName === null
                  ? `${row.siteCode} — ${row.siteName}`
                  : `${row.siteCode} — ${row.siteName} · ${row.zoneName}`,
            },
            { header: config.fieldUserLabel, render: (row) => row.fieldUserName },
            { header: 'Question', render: (row) => row.prompt },
            {
              header: 'Answer',
              render: (row) => (
                <div className="row">
                  <Badge tone="flagged">{row.answerLabel}</Badge>
                  {row.note !== null && <span className="muted">{row.note}</span>}
                </div>
              ),
            },
            { header: 'Form', render: (row) => row.formName },
          ]}
        />
      )}
    </Card>
  );
}
